<template>
  <v-card flat tile class="bulk-calculations-assumptions">
    <res-dialog :showDialog="showHelp"
                titleClass="resBlue darken-1 white--text"
                iconColor="white"
                cancelButtonText="Close"
                :cancelButtonOnly="true"
                cancelButtonColor="resBlue darken-1"
                :maxWidth="800"
                :setWidth="800"
                :setHeight="600"
                @cancel="closeHelp">
      <bulk-calculations-help :openSection="openHelpSection"></bulk-calculations-help>
    </res-dialog>
    <v-card-text>
      <v-card outlined rounded class="mx-auto pa-3 outline-thick outline-blue">
        <v-row class="bordered-bottom pl-2 py-2">
          <v-col class="d-flex" sm="11">
            <span class="text-adjust">
              I want to distribute amounts and/or assumption statements for
            </span>
            <span class="short">
              <res-select id="new-bulk-year-selector" attach="#new-bulk-year-selector" v-model="settings.year" :items="years" :loading="currentLoading" placeholder="select year"></res-select>
            </span>
            <span class="text-adjust">
              and I want my accounting basis to be
            </span>
            <span class="medium">
              <res-select id="new-bulk-accounting-basis-selector" attach="#new-bulk-accounting-basis-selector" v-model="settings.accountingBasisId" :items="accountingBasii" :loading="currentLoading" placeholder="select accounting basis"></res-select>
            </span>
          </v-col>
          <v-col class="d-flex" sm="1">
            <div class="help-controls" @click="openHelp(null)">
              <span class="help-label">Help</span>
              <v-icon color="var(--v-resBlue-darken1)" class="pb-1" small>mdi-help-circle</v-icon>
            </div>
          </v-col>
        </v-row>
        <v-row class="bordered-bottom pl-2 pt-2 pb-0">
          <v-col class="d-flex pb-2" sm="12">
            <span class="text-adjust">
              Apply to:
            </span>
            <v-combobox ref="newBulkPropertiesCombo" color="resBlue darken-1" dense outlined class="py-0" chips multiple persistent-placeholder placeholder="select properties and/or property group" v-model="settings.propsOrGroups" :items="selectablePropertiesAndPropGroups">
              <template v-slot:selection="{ item, index, attrs }">
                <v-chip v-bind="attrs" v-if="index < 3"
                        color="resBlue darken-1"
                        class="white--text"
                        close
                        @click:close="removeProp(item)">
                  {{ item.text }}
                </v-chip>
                <span v-if="index === 3"
                      class="grey--text text-caption">
                  (+{{ settings.propsOrGroups.length - 3 }} others)
                </span>
              </template>
            </v-combobox>
          </v-col>
        </v-row>
        <v-row class="bordered-bottom pl-2 py-2">
          <v-col class="d-flex" sm="11">
            <span class="text-adjust">
              I want to use the following method:
            </span>
            <span class="long">
              <res-select id="new-bulk-calculation-method" attach="#new-bulk-calculation-method" v-model="settings.method" :items="methodChoices" :loading="currentLoading" placeholder="select method"></res-select>
            </span>
            <span class="text-adjust">
              with the following 15-character max description:
            </span>
            <span class="medium">
              <res-text-field id="new-bulk-calculation-description" ref="descInput" :maxLength="15" validate-on-blur :rules="[v => (v || '').length <= 15 || 'Description must be 15 characters or less.', v => (v || '').trim().length >= 3 || 'Description must be 3 characters or more.']" :minLength="3" v-model="settings.description" :loading="currentLoading" placeholder="-"></res-text-field>
            </span>
          </v-col>
        </v-row>
        <v-row v-if="settings.method !== 4" class="bordered-bottom pl-2 py-2">
          <v-col class="d-flex" sm="11">
            <span class="text-adjust">
              Starting in
            </span>
            <span class="short">
              <res-select id="new-bulk-month-selector" attach="#new-bulk-month-selector" v-model="settings.startMonth" :items="months" :loading="currentLoading" placeholder="select month"></res-select>
            </span>
            <span class="text-adjust">
              I want
            </span>
            <span class="short">
              <res-number-input-field id="new-bulk-amount" :precision="2" v-model="settings.amount" prefix="$" :loading="currentLoading"></res-number-input-field>
            </span>
            <span class="text-adjust">
              {{amountPostDescriptor}} to populate
            </span>
            <span class="longer">
              <v-combobox ref="newBulkGLAccountIdCombo" color="resBlue darken-1" dense outlined class="py-0" :filter="(item, queryText, itemText) => isNaN(queryText) ? itemText.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) !== -1 : itemText.toLocaleLowerCase().startsWith(queryText.toLocaleLowerCase())" persistent-placeholder placeholder="select gl account" v-model="settings.glAccountId" :items="glAccounts" :loading="currentLoading">
                <template v-slot:selection="{ item }">

                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on"
                              v-show="showLockedIndicator"
                              class="resRed--text darken-2">mdi-alert-circle</v-icon>
                    </template>
                    <span>This GL Account is locked in one or more of the properties selected. You can not make a global formula to a locked GL Account unless you have the permission to Edit Locked GL Accounts.</span>
                  </v-tooltip>

                  <span class="pl-2">{{ item.text }}</span>
                </template>
              </v-combobox>
            </span>
            <span class="text-adjust">
              each month
            </span>
          </v-col>
        </v-row>
        <v-row v-if="settings.method === 4" class="bordered-bottom pl-2 py-2">
          <v-col class="d-flex" sm="11">
            <span class="text-adjust">
              And, I want to populate
            </span>
            <span class="longer">
              <v-combobox ref="newBulkGLAccountIdCombo_assumptionOnly" color="resBlue darken-1" dense outlined class="py-0" :filter="(item, queryText, itemText) => isNaN(queryText) ? itemText.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) !== -1 : itemText.toLocaleLowerCase().startsWith(queryText.toLocaleLowerCase())" persistent-placeholder placeholder="select gl account" v-model="settings.glAccountId" :items="glAccounts" :loading="currentLoading">
                <template v-slot:selection="{ item }">

                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on"
                              v-show="showLockedIndicator"
                              class="resRed--text darken-2">mdi-alert-circle</v-icon>
                    </template>
                    <span>This GL Account is locked in one or more of the properties selected. You can not make a global formula to a locked GL Account unless you have the permission to Edit Locked GL Accounts.</span>
                  </v-tooltip>

                  <span class="pl-2">{{ item.text }}</span>
                </template>
              </v-combobox>
            </span>
          </v-col>
        </v-row>
        <v-row class="bordered-bottom pl-2 py-2">
          <v-col class="d-flex" sm="11">
            <span class="text-adjust">
              <span v-if="settings.method !== 4">(Optional)</span> I want to add the following info to any existing assumption statement:
            </span>
            <span class="longest">
              <v-textarea auto-grow rows="1" class="pl-2 pt-0 mt-0" id="new-bulk-assumption-statement" v-model="settings.assumptionStatement" :loading="currentLoading" :rules="[v => (v || '').length <= 100 || 'Assumption statement must be 100 characters or less.', v => ((v || '').trim().length >= 3 || settings.method != 4) || 'Assumption statement must be 3 characters or more.']"></v-textarea>
            </span>
          </v-col>
        </v-row>
        <v-row class="bordered-bottom pl-2 py-2">
          <v-col class="d-flex" sm="11">
            <span class="text-adjust">
              <em><strong>Note:</strong> These values will be added to any existing GL Amounts. Once you click "CONTINUE" below, you will be able to view the exact distributions prior to sending them to the GL Accounts.</em>
            </span>
          </v-col>
        </v-row>
        <v-card-actions class="mt-2 pb-0">
          <v-row class="pl-2 pt-2 pb-0">
            <v-col class="d-flex" sm="10">

            </v-col>
            <v-col class="d-flex" sm="2">
              <v-btn height="30px" rounded elevation="0" outlined class="resGrey white--text mr-2" @click="clearSettings">
                CLEAR
              </v-btn>
              <v-btn height="30px" :loading='currentLoading' rounded elevation="0" :disabled="continueDisabled" class="resGreen white--text" @click="apply">
                CONTINUE
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>

      <v-card outlined rounded class="mx-auto mt-5">
        <v-tabs v-model="subAreaTab">
          <v-tab>
            PREVIOUSLY DISTRIBUTED
          </v-tab>
        </v-tabs>
      </v-card>

      <v-tabs-items v-model="subAreaTab">
        <v-tab-item>
          <v-card outlined rounded class="mx-auto pa-3 mb-2 mt-5 deep-margin-bottom">
            <v-row class="pl-2 py-2">
              <v-col class="d-flex" sm="12">
                <div class="page-controls">
                  <div class="previous-years-controls">
                    <span class="previous-years-label">Show me Bulk Calculations / Assumptions for </span>
                    <span class="short">
                      <bulk-calculations-assumptions-year-selector :value="previousAppliedYear" :triggerLoad="triggerYearLoad" @input="reloadPreviousForYear" class="inline-block"></bulk-calculations-assumptions-year-selector>
                    </span>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card>

          <div v-show="previousAppliedYear" class="bump-through-deep-margin">
            <template v-for="(prevItem, i) in previousAppliedItems">
              <v-card outlined rounded class="outline-thick mx-auto pa-3 mb-4" :key="prevItem.bulkCalculationId">
                <v-row class="bordered-bottom pl-2 py-2">
                  <v-col sm="12">
                    <strong>
                      Note: Below settings were applied by {{prevItem.appliedByName}} on {{localizeDate(prevItem.appliedTimestamp) | moment('MM/DD/YYYY hh:mm:ss A')}}
                    </strong>
                  </v-col>
                </v-row>

                <v-row class="bordered-bottom pl-2 py-2">
                  <v-col class="d-flex" sm="11">
                    <span class="text-adjust">
                      I want to distribute amounts and/or assumption statements for
                    </span>
                    <span class="short">
                      <res-select readonly :id="'new-bulk-year-selector' + i" :attach="'#new-bulk-year-selector' + i" v-model="prevItem.year" :items="years" :loading="currentLoading" placeholder="select"></res-select>
                    </span>
                    <span class="text-adjust">
                      and I want my accounting basis to be
                    </span>
                    <span class="short">
                      <res-select readonly :id="'new-bulk-accounting-basis-selector' + i" :attach="'#new-bulk-accounting-basis-selector' + i" v-model="prevItem.accountingBasisId" :items="accountingBasii" :loading="currentLoading" placeholder="select"></res-select>
                    </span>
                  </v-col>
                </v-row>
                <v-row class="bordered-bottom pl-2 pt-2 pb-0">
                  <v-col class="d-flex pb-2" sm="12">
                    <span class="text-adjust">
                      Apply to:
                    </span>
                    <v-combobox readonly :ref="'newBulkPropertiesCombo' + i" color="resBlue darken-1" dense outlined class="py-0" chips multiple v-model="prevItem.propsOrGroups" :items="selectablePropertiesAndPropGroups">
                      <template v-slot:selection="{ item, index, attrs }">
                        <v-chip v-bind="attrs" v-if="index < 3"
                                color="resBlue darken-1"
                                class="white--text"
                                close
                                @click:close="removeProp(item)">
                          {{ item.text }}
                        </v-chip>
                        <span v-if="index === 3"
                              class="grey--text text-caption">
                          (+{{ prevItem.propsOrGroups.length - 3 }} others)
                        </span>
                      </template>
                    </v-combobox>
                  </v-col>
                </v-row>
                <v-row class="bordered-bottom pl-2 py-2">
                  <v-col class="d-flex" sm="11">
                    <span class="text-adjust">
                      I want to use the following method:
                    </span>
                    <span class="medium">
                      <res-select readonly :id="'new-bulk-calculation-method' + i" :attach="'#new-bulk-year-selector' + i" v-model="prevItem.method" :items="methodChoices" :loading="currentLoading" placeholder="select"></res-select>
                    </span>
                    <span class="text-adjust">
                      with the following 15-character max description:
                    </span>
                    <span class="medium">
                      <res-text-field readonly :id="'new-bulk-calculation-description' + i" :attach="'#new-bulk-accounting-basis-selector' + i" v-model="prevItem.description" :loading="currentLoading"></res-text-field>
                    </span>
                  </v-col>
                </v-row>
                <v-row v-if="prevItem.method !== 4" class="bordered-bottom pl-2 py-2">
                  <v-col class="d-flex" sm="11">
                    <span class="text-adjust">
                      Starting in
                    </span>
                    <span class="short">
                      <res-select readonly :id="'new-bulk-month-selector' + i" :attach="'#new-bulk-month-selector' + i" v-model="prevItem.startMonth" :items="months" :loading="currentLoading" placeholder="select"></res-select>
                    </span>
                    <span class="text-adjust">
                      I want
                    </span>
                    <span class="short">
                      <res-number-input-field readonly :id="'new-bulk-amount' + i" :precision="2" :attach="'#new-bulk-accounting-basis-selector' + i" :value="prevItem.amount.toFixed(2)" prefix="$" :loading="currentLoading" placeholder="select"></res-number-input-field>
                    </span>
                    <span class="text-adjust">
                      {{getAmountPostDescriptor(prevItem.method)}} to populate
                    </span>
                    <span class="long">
                      <res-select readonly :id="'new-bulk-gl-account' + i" :attach="'#new-bulk-accounting-basis-selector' + i" v-model="prevItem.glAccountId" :items="glAccounts" :loading="currentLoading" placeholder="select"></res-select>
                    </span>
                    <span class="text-adjust">
                      each month
                    </span>
                  </v-col>
                </v-row>
                <v-row v-if="prevItem.method === 4" class="bordered-bottom pl-2 py-2">
                  <v-col class="d-flex" sm="11">
                    <span class="text-adjust">
                      And, I want to populate
                    </span>
                    <span class="long">
                      <res-select readonly :id="'new-bulk-gl-account' + i" :attach="'#new-bulk-accounting-basis-selector' + i" v-model="prevItem.glAccountId" :items="glAccounts" :loading="currentLoading" placeholder="select"></res-select>
                    </span>
                  </v-col>
                </v-row>
                <v-row class="bordered-bottom pl-2 py-2">
                  <v-col class="d-flex" sm="11">
                    <span class="text-adjust">
                      <span v-if="prevItem.method !== 4">(Optional)</span> I want to add the following info to any existing assumption statement:
                    </span>
                    <span class="longest">
                      <v-textarea auto-grow rows="1" class="pl-2 pt-0 mt-0" readonly :id="'new-bulk-assumption-statement' + i" :attach="'#new-bulk-accounting-basis-selector' + i" v-model="prevItem.assumptionStatement" :loading="currentLoading"></v-textarea>
                    </span>
                  </v-col>
                </v-row>
                <v-row class="pl-2 py-2" v-if="prevItem && prevItem.bulkCalculateApplications && prevItem.bulkCalculateApplications.length > 0">
                  <v-col>
                    <span class="text-adjust">
                      <a class="underline" @click="openDistributionDialogDialog(prevItem)">View Property Distributions.</a>
                    </span>
                  </v-col>
                </v-row>
              </v-card>
            </template>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </v-card-text>
    <confirm-bulk-calculations-assumptions-dialog v-if="showConfirmDialog"
                                                  :showDialog="showConfirmDialog"
                                                  :errors="settings.errors"
                                                  :method="settings.method"
                                                  :assumptionStatement="settings.assumptionStatement"
                                                  :bulkApplications="settings.bulkApplications"
                                                  :maxWidth="800"
                                                  :setWidth="800"
                                                  @confirm="confirm"
                                                  @cancel="closeConfirmDialog">
    </confirm-bulk-calculations-assumptions-dialog>

    <confirm-bulk-calculations-assumptions-dialog v-if="showDistributionDialog"
                                                  :showDialog="showDistributionDialog"
                                                  :errors="[]"
                                                  :method="distributionMethod"
                                                  :assumptionStatement="distributionAssumptionStatement"
                                                  :bulkApplications="distributions"
                                                  :maxWidth="800"
                                                  :setWidth="800"
                                                  past
                                                  @cancel="closeDistributionDialog">
    </confirm-bulk-calculations-assumptions-dialog>
    <v-overlay :value="overlay">
      <v-container>
        <v-row class="fill-height"
               align-content="center"
               justify="center">
          <v-col cols="12" dark>
            <div id="Loading"><div id="LoadingImage" class="shadow ui-corner-all"></div></div>
          </v-col>
        </v-row>
      </v-container>
    </v-overlay>
  </v-card>
</template>
<script>
  import Localizable from '@/mixins/Localizable.js';
  import HelpableMixin from '@/mixins/Helpable.js';

  import ResSelect from '@/components/base/ResSelect.vue';
  import ResTextField from '@/components/base/ResTextField.vue';
  import ResNumberInputField from '@/components/base/ResNumberInputField.vue';
  import ResDialog from '@/components/base/ResDialog.vue';
  import ConfirmBulkCalculationsAssumptionsDialog from './dialogs/ConfirmBulkCalculationsAssumptionsDialog.vue';
  import BulkCalculationsAssumptionsYearSelector from './BulkCalculationsAssumptionsYearSelector.vue';
  import BulkCalculationsHelp from '@/components/help/BulkCalculationsHelp.vue';

  import budgetService from '@/services/budget';
  import propertyService from '@/services/property';
  import glAccountService from '@/services/gl-account';
  import budgetGLService from '@/services/budget-gl-account';
  import UserService from '@/services/users';
  import { mapActions } from 'vuex';
  import { mapGetters } from 'vuex';
  export default {
    name: 'BulkCalculationsAssumptions',
    mixins: [Localizable, HelpableMixin],
    components: {
      ResSelect,
      ResTextField,
      ResNumberInputField,
      ResDialog,
      ConfirmBulkCalculationsAssumptionsDialog,
      BulkCalculationsAssumptionsYearSelector,
      BulkCalculationsHelp
    },
    data: function () {
      return {
        overlay: false,
        showConfirmDialog: false,
        showDistributionDialog: false,
        years: [],
        months: [
          { text: 'January ', value: 1 },
          { text: 'February ', value: 2 },
          { text: 'March ', value: 3 },
          { text: 'April ', value: 4 },
          { text: 'May ', value: 5 },
          { text: 'June ', value: 6 },
          { text: 'July ', value: 7 },
          { text: 'August ', value: 8 },
          { text: 'September ', value: 9 },
          { text: 'October ', value: 10 },
          { text: 'November ', value: 11 },
          { text: 'December ', value: 12 }
        ],
        accountingBasii: [
          { text: 'Accrual', value: 1 },
          { text: 'Cash', value: 2 }
        ],
        methodChoices: [
          { text: 'Flat Fee', value: 1 },
          { text: 'Price per Unit', value: 2 },
          { text: '% Allocation by Unit Count', value: 3 },
          { text: 'Assumption Only (no calculations)', value: 4 }
        ],
        selectablePropertiesAndPropGroups: [],
        settings: {
          year: null,
          startMonth: 1,
          accountingBasisId: null,
          method: 0,
          amount: null,
          glAccountId: null,
          description: '',
          assumptionStatement: '',
          propsOrGroups: [],
          errors: []
        },
        previousAppliedYear: null,
        previousAppliedItems: [],
        distrubitions: [],
        errors: [],
        triggerYearLoad: 0,
        subAreaTab: 0,
        glAccounts: [],
        distributionMethod: 0,
        distributionAssumptionStatement: '',
        glAccountLocks: {},
        hasEditLockedGLPermission: false,
        showLockedIndicator: false
      };
    },
    props: {
    },
    methods: {
      ...mapActions(['changeLoading']),
      async loadInitialData() {
        this.changeLoading(true);
        this.years = [];

        var years = await budgetService.getExistingBudgetYears();
        this.years = (((years) || []).map((y) => { return { text: y, value: +y }; }));

        let propertyData = await propertyService.getDropdown(true);
        if (propertyData && propertyData.data) {
          this.selectablePropertiesAndPropGroups = propertyData.data;
        } else {
          this.selectablePropertiesAndPropGroups = [];
        }
        await this.loadPreviousAppliedItems();

        var userSettings = await UserService.getUserSettings(true);
        this.hasEditLockedGLPermission = userSettings && userSettings.permissions && userSettings.permissions.EditLockedGLAccounts && userSettings.permissions.EditLockedGLAccounts.hasPermission;

        await this.loadGlAccounts();

        this.changeLoading(false);
      },
      async loadPreviousAppliedItems() {
        if (this.previousAppliedYear == null) {
          this.previousAppliedItems = [];
          return;
        }
        this.changeLoading(true);
        let bulkData = await budgetGLService.getAllBulkCalculationsAssumptions(this.previousAppliedYear);
        if (bulkData && bulkData.data) {
          this.previousAppliedItems = bulkData.data;
        } else {
          this.previousAppliedItems = [];
        }
        this.changeLoading(false);
      },
      async loadGlAccounts() {
        this.glAccountLocks = {};
        this.glAccounts = (await glAccountService.getGlAccounts(null, null, true))
          .sort((glA, glB) => { if (glA.number < glB.number) { return -1; } if (glA.number > glB.number) { return 1; } return 0; })
          .map((gl) => { this.addGlAccountLock(gl); return { text: gl.number + ' - ' + gl.name, value: gl.glAccountId }; });
      },
      removeProp(item) {
        const index = this.settings.propsOrGroups.indexOf(item)
        if (index >= 0) this.settings.propsOrGroups.splice(index, 1)
      },
      clearSettings() {
        this.previousAppliedYear = null;
        this.settings = {
          year: null,
          startMonth: 1,
          accountingBasisId: null,
          method: 0,
          amount: null,
          glAccountId: null,
          description: '',
          assumptionStatement: '',
          propsOrGroups: [],
          errors: []
        };
      },
      async apply() {
        this.changeLoading(true);
        // send api call for checking all props and groups selected
        var checkResponse = await budgetGLService.checkForBulkCalculationsAssumptions(this.settings);
        if (!checkResponse || !checkResponse.errors || checkResponse.errors.length > 0) {
          this.settings.errors = (checkResponse && checkResponse.errors) ? checkResponse.errors.sort((a, b) => {
            if (a.propertyAbbreviation < b.propertyAbbreviation) { return -1; }
            if (a.propertyAbbreviation > b.propertyAbbreviation) { return 1; }
            return 0;
          }) : [{ propertyAbbreviation: '---', error: 'Unable to retrieve data from ResMan Core. Please try again or contact ResMan Support.' }];
        }
        else {
          this.settings.errors = []
        }
        if (checkResponse.bulkApplications && checkResponse.bulkApplications.length > 0) {
          this.settings.bulkApplications = checkResponse.bulkApplications;
        }
        this.openConfirmDialog();
        this.changeLoading(false);
      },
      openConfirmDialog() {
        this.showConfirmDialog = true;
      },
      closeConfirmDialog() {
        this.showConfirmDialog = false;
      },
      async confirm(assumptionStatement) {
        // update statement from dialog input
        this.settings.assumptionStatement = assumptionStatement;

        this.showConfirmDialog = false;
        this.overlay = true;
        //start creating those budgets, minus the error props
        var response = await budgetGLService.applyBulkCalculationsAssumptions(this.settings);
        if (response) {
          // refresh prev items display
          this.triggerYearLoad++;
          console.debug("succeeded");
          this.clearSettings();
          this.overlay = false;
        } else {
          console.debug("failed");
          this.overlay = false;
        }
      },
      async reloadPreviousForYear(year) {
        this.previousAppliedYear = year;
        await this.loadPreviousAppliedItems();
      },
      openDistributionDialogDialog(distribution) {
        this.distributionMethod = distribution.method;
        this.distributionAssumptionStatement = distribution.assumptionStatement;
        distribution.bulkCalculateApplications.sort((a, b) => {
          if (a.propertyAbbreviation < b.propertyAbbreviation) { return -1; }
          if (a.propertyAbbreviation > b.propertyAbbreviation) { return 1; }
          return 0;
        });
        this.distributions = distribution.bulkCalculateApplications;
        this.showDistributionDialog = true;
      },
      closeDistributionDialog() {
        this.showDistributionDialog = false;
        this.distributions = [];
      },
      getAmountPostDescriptor(method) {
        switch (method) {
          case 1: return '';
          case 2: return 'per unit';
          case 3: return 'allocated based on unit count';
          case 4: return '';
        }
        return '';
      },
      addGlAccountLock(gl) {
        if (this.hasEditLockedGLPermission) {
          return;
        }
        if (!gl.glAccountLocks || gl.glAccountLocks.length === 0) {
          return;
        }
        this.glAccountLocks[gl.glAccountId] = gl.glAccountLocks || [];
      },
      checkLocks() {
        if (!(this.settings && this.settings.glAccountId && this.settings.propsOrGroups && this.settings.accountingBasisId && this.settings.year)) {
          this.showLockedIndicator = false;
          return;
        }
        var glLocks = this.glAccountLocks && this.glAccountLocks[this.settings.glAccountId.value];
        if (glLocks && glLocks.filter((gl) => gl.year == this.settings.year && gl.accountingBasisId == this.settings.accountingBasisId && this.settings.propsOrGroups && this.settings.propsOrGroups.filter((pg) => pg.value.toLowerCase() == gl.propertyId.toLowerCase()).length > 0).length > 0) {
          this.showLockedIndicator = true;
        }
        else {
          this.showLockedIndicator = false;
        }
      }
    },
    computed: {
      ...mapGetters(['currentLoading']),
      amountPostDescriptor() {
        return this.getAmountPostDescriptor(this.settings && this.settings.method);
      },
      continueDisabled() {
        if (
          this.settings.year != null &&
          (this.settings.startMonth != null || this.settings.method === 4) &&
          this.settings.accountingBasisId != null &&
          (this.settings.amount || this.settings.method === 4) &&
          this.settings.method &&
          (this.settings.description && this.settings.description.length >=3 && this.settings.description.length <= 15) &&
          this.settings.glAccountId && this.settings.glAccountId.value && this.settings.propsOrGroups.length > 0 &&
          (this.settings.method !== 4 || (this.settings.assumptionStatement && this.settings.assumptionStatement.length >= 3)) &&
          !this.showLockedIndicator
        ) {
          return false;
        } else {
          return true;
        }
      },
    },
    async created() {
      this.$eventHub.$on('replicatedEntitiesSynced', await this.loadInitialData);
    },
    async mounted() {
      await this.loadInitialData();
    },
    watch: {
      'settings.propsOrGroups': function (newVal) {
        this.checkLocks();
        if (newVal && newVal.length > 0) {
          var newNewVal = newVal.filter((nV) => typeof nV == 'object');
          if (newNewVal.length != newVal.length) {
            this.settings.propsOrGroups = newNewVal;
          }
        }
      },
      'settings.year': function () {
        this.checkLocks();
      },
      'settings.accountingBasisId': function () {
        this.checkLocks();
      },
      'settings.glAccountId': function () {
        this.checkLocks();
      }
    }
  };
</script>
<style lang="scss" scoped>
  #Loading {
    z-index: 1010;
    position: absolute;
    top: 50%;
    left: 50%;
    margin-left: -120px;
    border: 1px solid #D7D7D7;
  }
  .bordered-bottom {
      border-bottom: 1px solid var(--v-resGrey-lighten1);
  }
  strong {
      color: var(--v-resGrey-darken1);
  }
  .text-adjust {
      padding-top:5px;
      padding-left:5px;
      padding-right:5px;
  }
  .underline {
      text-decoration:underline;
  }
  .deep-margin-bottom {
      margin-bottom: 200px !important;
  }
  .bump-through-deep-margin {
      margin-top:-180px;
  }
  #LoadingImage {
    background: #fff url(../../assets/ajax-loader.gif) no-repeat center;
    width: 240px;
    height: 40px;
    margin: auto;
  }
  .outline-thick {
    border: 3px solid var(--v-resGrey-lighten1);
    border-radius: 10px !important;
  }
  .outline-blue {
      border-color: var(--v-resBlue-darken1) !important;
  }
  span.short {
      max-width: 160px;
  }
  span.medium {
      max-width: 260px;
      min-width: 260px;
  }
  span.long {
    max-width: 320px;
    min-width: 320px;
  }
  span.long {
      max-width: 450px;
  }
  span.longest {
      min-width: 600px;
  }
  span.longer {
      max-width: 600px;
      min-width: 600px;
  }
  .inline-block {
      display:inline-block;
  }
</style>

<style>
  .bulk-calculations-assumptions :not(.v-input--is-focused).v-select--chips input {
    max-height: unset !important;
  }
</style>
